import React, { useState, useEffect } from 'react';
import { Mic, AlertCircle, Check } from 'lucide-react';
import { SpeechToTextButtonProps } from '../../types/speech';
import { logger } from '../../utils/logger';


/**
 * SpeechToTextButton - Composant pour la reconnaissance vocale
 *
 * Fonctionnalités :
 * - Reconnaissance vocale multilingue
 * - Interface utilisateur intuitive
 * - Gestion des erreurs
 * - Support des navigateurs modernes
 *
 * @param onTextReceived - Callback appelé avec le texte reconnu
 * @param currentLanguage - Langue actuelle pour la reconnaissance (défaut: 'fr-FR')
 * @param isDisabled - Désactiver le bouton
 * @param placeholder - Texte alternatif pour l'accessibilité
 * @param className - Classes CSS additionnelles
 */
export const SpeechToTextButton: React.FC<SpeechToTextButtonProps> = ({
  onTextReceived,
  currentLanguage = 'fr-FR',
  isDisabled = false,
  placeholder = 'Cliquez pour dicter',
  className = '',
  onListeningChange
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDisabledByNetwork, setIsDisabledByNetwork] = useState(false);
  const [micPermission, setMicPermission] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const [recognition, setRecognition] = useState<any>(null);

  // Vérifier le support du navigateur au montage
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const supported = !!SpeechRecognition;

    setIsSupported(supported);

    if (!supported) {
      logger.warn('SpeechToTextButton', 'Speech recognition not supported in this browser');
    }
  }, []);


  // Détecte l'état d'autorisation du microphone et écoute les changements
  useEffect(() => {
    const permissions = (navigator as any).permissions;
    if (!permissions || !permissions.query) {
      return;
    }
    let mounted = true;
    permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status: PermissionStatus) => {
        if (!mounted) return;
        setMicPermission(status.state as any);
        status.onchange = () => setMicPermission(status.state as any);
      })
      .catch(() => {
        // Permissions API non supportée ou bloquée
        setMicPermission('unknown');
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Demande l'accès au microphone si nécessaire
  const ensureMicAccess = async (): Promise<boolean> => {
    try {
      if (micPermission !== 'granted') {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicPermission('granted');
      }
      return true;
    } catch (e: any) {
      setMicPermission('denied');
      setError(
        "Accès au microphone refusé. Cliquez sur le cadenas du navigateur et autorisez le micro."
      );
      logger.warn('SpeechToTextButton', 'Microphone permission denied or failed', e);
      return false;
    }
  };

  /**
   * Réactive la reconnaissance vocale après désactivation
   */
  const reactivateSpeechRecognition = () => {
    setIsDisabledByNetwork(false);
    setError(null);
    logger.info('SpeechToTextButton', 'Speech recognition reactivated by user');
  };

  /**
   * Arrête la reconnaissance vocale
   */
  const stopListening = () => {
    if (recognition) {
      recognition.stop();
      setRecognition(null);
    }
    setIsListening(false);
    if (onListeningChange) onListeningChange(false);
  };

  /**
   * Démarre la reconnaissance vocale
   */
  const startListening = async () => {
    if (!isSupported) {
      setError('La reconnaissance vocale n\'est pas supportée dans ce navigateur');
      return;
    }

    if (isDisabled || isDisabledByNetwork) {
      if (isDisabledByNetwork) {
        setError('Reconnaissance vocale temporairement désactivée en raison d\'erreurs réseau. Rechargez la page pour réactiver.');
      }
      return;
    }

    // S'assure que le micro est autorisé avant de démarrer
    const hasMic = await ensureMicAccess();
    if (!hasMic) {
      return;
    }

    setError(null);

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setError('La reconnaissance vocale n\'est pas supportée dans ce navigateur');
        return;
      }
      const newRecognition = new SpeechRecognition();
      setRecognition(newRecognition);

      // Configuration de la reconnaissance
      newRecognition.continuous = true;
      newRecognition.interimResults = true;
      newRecognition.lang = currentLanguage;
      newRecognition.maxAlternatives = 1;

      // Gestionnaires d'événements
      newRecognition.onstart = () => {
        setIsListening(true);
        if (onListeningChange) onListeningChange(true);
        logger.debug('SpeechToTextButton', 'Speech recognition started', { language: currentLanguage });
      };

      newRecognition.onend = () => {
        setIsListening(false);
        if (onListeningChange) onListeningChange(false);
        setRecognition(null);
        logger.debug('SpeechToTextButton', 'Speech recognition ended');
      };

      newRecognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        logger.debug('SpeechToTextButton', 'Speech recognized', { transcript, confidence: event.results[0][0].confidence });

        if (transcript && transcript.trim()) {
          onTextReceived(transcript.trim());
        }
      };

      newRecognition.onerror = (event: any) => {
        setIsListening(false);
        setRecognition(null);
        const errorMessage = getErrorMessage(event.error);
        setError(errorMessage);
        logger.error('SpeechToTextButton', 'Speech recognition error', { error: event.error, message: errorMessage });

        // Gestion spéciale pour les erreurs réseau
        if (event.error === 'network') {
          setIsDisabledByNetwork(true);
          logger.warn('SpeechToTextButton', 'Speech recognition disabled due to network issues');
        }
      };

      newRecognition.start();
    } catch (error) {
      setIsListening(false);
      setError('Erreur lors du démarrage de la reconnaissance vocale');
      logger.error('SpeechToTextButton', 'Failed to start speech recognition', error);
    }
  };


  /**
   * Convertit les erreurs en messages utilisateur
   */
  const getErrorMessage = (error: string): string => {
    switch (error) {
      case 'not-allowed':
        return 'Accès au microphone refusé. Veuillez autoriser l\'accès.';
      case 'no-speech':
        return 'Aucune parole détectée. Veuillez réessayer.';
      case 'audio-capture':
        return 'Erreur de capture audio. Vérifiez votre microphone.';
      case 'network':
        return 'Erreur réseau. La reconnaissance vocale nécessite une connexion internet. Vérifiez votre connexion.';
      case 'service-not-allowed':
        return 'Service de reconnaissance vocale non autorisé. Essayez un autre navigateur.';
      case 'bad-grammar':
        return 'Erreur de configuration linguistique. Essayez de changer de langue.';
      default:
        return `Erreur de reconnaissance vocale: ${error}`;
    }
  };

  /**
   * Obtient l'emoji du drapeau pour la langue
   */
  const getLanguageFlag = (lang: string): string => {
    // console.log('Language code received:', lang); // Debug retiré
    
    const languageFlags: Record<string, string> = {
      // Français
      'fr-FR': '🇫🇷',
      'fr-CA': '🇨🇦',
      'fr': '🇫🇷', // Ajout du code court
      
      // Anglais
      'en-US': '🇺🇸',
      'en-GB': '🇬🇧',
      'en': '🇺🇸', // Ajout du code court
      
      // Autres langues
      'es-ES': '🇪🇸',
      'es-MX': '🇲🇽',
      'es': '🇪🇸',
      'de-DE': '🇩🇪',
      'de': '🇩🇪',
      'it-IT': '🇮🇹',
      'it': '🇮🇹',
      'pt-BR': '🇧🇷',
      'pt-PT': '🇵🇹',
      'pt': '🇧🇷',
      'nl-NL': '🇳🇱',
      'nl': '🇳🇱',
      'ru-RU': '🇷🇺',
      'ru': '🇷🇺',
      'ar-SA': '🇸🇦',
      'ar': '🇸🇦',
      'zh-CN': '🇨🇳',
      'zh': '🇨🇳',
      'ja-JP': '🇯🇵',
      'ja': '🇯🇵',
      'ko-KR': '🇰🇷',
      'ko': '🇰🇷'
    };

    return languageFlags[lang] || `🌐 (${lang})`;
  };

  // Rendu conditionnel selon le support et l'état
  if (!isSupported) {
    return (
      <button
        type="button"
        disabled
        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed ${className}`}
        title="Reconnaissance vocale non supportée"
        aria-label="Reconnaissance vocale non disponible"
      >
        <AlertCircle size={18} />
      </button>
    );
  }

  return (
    <div className="relative flex justify-end">
      {/* Bouton microphone simple */}
      <button
        type="button"
        onClick={micPermission === 'prompt' || micPermission === 'denied' ? ensureMicAccess : (isListening ? stopListening : startListening)}
        disabled={micPermission !== 'prompt' && micPermission !== 'denied' && (isDisabled || isDisabledByNetwork)}
        className={`
          inline-flex items-center justify-center w-10 h-10 rounded-lg border-2 transition-colors duration-200
          ${(micPermission === 'prompt' || micPermission === 'denied')
            ? 'border-amber-500 bg-amber-500 text-white hover:bg-amber-600'
            : isListening
              ? 'border-green-500 bg-green-500 text-white'
              : 'border-gray-400 bg-gray-400 text-white hover:bg-gray-500'
          }
          ${(micPermission !== 'prompt' && micPermission !== 'denied' && (isDisabled || isDisabledByNetwork))
            ? 'opacity-50 cursor-not-allowed bg-gray-300 border-gray-300'
            : 'cursor-pointer shadow-md hover:shadow-lg'
          }
        `}
        title={micPermission === 'prompt' || micPermission === 'denied'
          ? 'Autoriser le microphone'
          : isListening
            ? `Écoute en cours... (${getLanguageFlag(currentLanguage)})`
            : `${placeholder} (${getLanguageFlag(currentLanguage)})`
        }
        aria-label={micPermission === 'prompt' || micPermission === 'denied'
          ? 'Autoriser le microphone'
          : isListening
            ? 'Arrêter la reconnaissance vocale'
            : 'Démarrer la reconnaissance vocale'
        }
      >
        {isListening ? (
          <Check size={18} />
        ) : (
          <Mic size={18} />
        )}
      </button>

      {/* Message d'erreur */}
      {error && (
        <div className="absolute top-full right-0 mt-2 w-full max-w-xs bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs text-red-700 shadow-sm z-20">
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{error}</span>
            <div className="flex gap-1">
              {isDisabledByNetwork && (
                <button
                  type="button"
                  onClick={reactivateSpeechRecognition}
                  className="text-blue-600 hover:text-blue-800 font-medium underline text-xs"
                  aria-label="Réactiver la reconnaissance vocale"
                >
                  Réactiver
                </button>
              )}
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 text-sm leading-none"
                aria-label="Fermer le message d'erreur"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
